import React, { memo, useEffect, useState } from 'react';
import Cookies from 'js-cookie';

import { Box, Icon, Tag, Text } from '@zillow/constellation-10';

import AdaptiveAnchor from '../AdaptiveAnchor';

import HeaderNavigationClickstreamEvents from '../../utils/clickstream';
import { navigationItemStyles } from './styles';

export type Props = {
  target?: string;
  title?: string;
  path?: string | null;
  escape?: boolean;
  isActive?: boolean;
  isMobileWeb?: boolean;
  noMarginBottom?: boolean;
  useAbsolutePaths?: boolean;
  newTagCookieName?: string;
  showNewTag?: boolean;
  // Link component from react-router or next.js
  link?: React.ComponentType<React.PropsWithChildren<{ to: string }>>;

  ActiveIcon?: React.ReactNode;
  InactiveIcon?: React.ReactNode;
  TrailingIcon?: React.ReactNode;

  onClick?: (event?: React.MouseEvent<HTMLElement, MouseEvent> | undefined) => void | null;
};

const NavigationItem = ({
  title,
  target = '_self',
  path = null,
  escape = false,
  isActive = false,
  isMobileWeb = false,
  useAbsolutePaths,
  link,
  noMarginBottom = false,
  newTagCookieName,
  showNewTag = true,
  ActiveIcon,
  InactiveIcon,
  TrailingIcon,
  onClick,
}: Props) => {
  // Initialize state to false (hidden) to prevent flash for returning users
  const [shouldShowNewTag, setShouldShowNewTag] = useState(false);

  // When showNewTag is true: show tag only if cookie not set.
  // When false (e.g. canViewOnboardingFlow true): set cookie so tag won't reappear if flag later flips.
  useEffect(() => {
    if (newTagCookieName) {
      if (showNewTag && Cookies.get(newTagCookieName) !== 'true') {
        setShouldShowNewTag(true);
      } else {
        if (!showNewTag) {
          Cookies.set(newTagCookieName, 'true', { expires: 365 });
        }
        setShouldShowNewTag(false);
      }
    }
  }, [showNewTag, newTagCookieName]);

  const handleClick = (event: React.MouseEvent<HTMLElement, MouseEvent> | undefined) => {
    // Set cookie and state to hide tag after first click
    if (shouldShowNewTag && newTagCookieName) {
      Cookies.set(newTagCookieName, 'true', { expires: 365 }); // 1 year
      setShouldShowNewTag(false);
    }

    if (onClick) {
      onClick(event);
    }
  };

  const renderNavigationItem = () => {
    return (
      <Box
        data-testid={isActive ? 'NavigationItem-Active' : 'NavigationItem'}
        tabIndex={0}
        css={navigationItemStyles.raw({ isActive: isActive && !isMobileWeb })}
      >
        {Boolean(ActiveIcon || InactiveIcon) && (
          <Icon css={{ marginRight: '200' }} size="md">
            {isActive ? ActiveIcon : InactiveIcon}
          </Icon>
        )}
        <Text css={{ textStyle: isActive && !isMobileWeb ? 'body-bold' : 'body', color: 'inherit' }}>{title}</Text>
        {shouldShowNewTag && (
          <Tag css={{ marginLeft: 'auto' }} size="sm" tone="purple">
            New
          </Tag>
        )}
        {Boolean(TrailingIcon) && (
          <Icon css={{ position: 'relative', top: '1px', marginLeft: 'auto', lineHeight: 0 }}>{TrailingIcon}</Icon>
        )}
      </Box>
    );
  };

  return (
    <Box css={{ marginBottom: noMarginBottom ? 0 : '200', color: 'inherit', textDecoration: 'none', width: '100%' }}>
      <AdaptiveAnchor
        data-testid="NavigationItem"
        target={target}
        title={title}
        escape={escape}
        link={link}
        path={path}
        useAbsolutePaths={useAbsolutePaths}
        onClick={handleClick}
        data-eventid={HeaderNavigationClickstreamEvents.ClickNavigationLink.data_event_id}
      >
        {renderNavigationItem()}
      </AdaptiveAnchor>
    </Box>
  );
};

export default memo(NavigationItem);
